import { CONSUMER_BANKING_TEAM_CODES, GIT_RELEASE_BRANCH } from '@/config'
import { getRouter, paths } from '@/router'
import ApiService from '@/service'
import type { DataType, FunctionUsedKeys, KeyType, ParamKeyMeta, ScriptKeysLookup, UsedKeys, UsedValues } from '@/types'
import { LATEST_SUPPORTED_USER_FUNCTION_FORMAT_VERSION } from '@/utils/constants'
import notifications from '@/utils/notifications'
import * as testValueUtils from '@/utils/testValueUtils'
import { deepClone, draftBranchName } from '@/utils/utils'
import { moduleActionContext } from '@/vuex'
import { AuthState } from '@/vuex/authModule'
import type {
  CreateFunctionRequest,
  PullRequest,
  TestResult,
  UserFunction,
  UserFunctionTest,
} from '@klarna/cbrms-policy-rules-manager-client-ts/api'
import axios from 'axios'
import { defineActions, defineGetters, defineModule, defineMutations } from 'direct-vuex'
import pick from 'lodash/pick'

export interface UserFunctionState {
  currentUserFunction: UserFunction
  pristineUserFunction: UserFunction
  userFunctions: UserFunction[]
  isLoading: boolean
  currentBranch: string
  currentPr?: PullRequest
  insertedFunction?: {
    functionName: string
    usageExample: string
  }
  insertedVariable?: string
  isSaving: boolean
  currentUserFunctionBranches: string[]
  usedValues: UsedValues
  consoleResult?: TestResult
  testResults?: (TestResult | undefined)[]
  functionToHighlight?: string
  inputToHighlight?: string
  isEditingTestName: boolean
  isDraftOutdated?: boolean
  isPRMerging: boolean
  isPRLoading: boolean
}

const restoreKey = (id: string, branch: string): string => {
  return `${id}/${branch}`
}

let pollingTimer: number | undefined = undefined

export const userFunctionActionContext = (context: any) => moduleActionContext(context, userFunctionModule)

const userFunctionModule = defineModule({
  namespaced: true,

  state: {
    currentUserFunction: {} as UserFunction,
    pristineUserFunction: {} as UserFunction,
    userFunctions: [],
    isLoading: false,
    currentBranch: GIT_RELEASE_BRANCH,
    currentPr: undefined,
    insertedFunction: undefined,
    insertedVariable: undefined,
    isSaving: false,
    currentUserFunctionBranches: [],
    usedValues: { inputs: {}, helpers: {} },
    consoleResult: undefined,
    testResults: undefined,
    functionToHighlight: undefined,
    inputToHighlight: undefined,
    isEditingTestName: false,
    isDraftOutdated: false,
    isPRMerging: false,
    isPRLoading: false,
  } as UserFunctionState,

  getters: defineGetters<UserFunctionState>()({
    isMyBranch(state: UserFunctionState, getters: any, rootState: any) {
      const auth = rootState.auth as AuthState
      const username = auth.user && auth.user.username
      return username === state.currentBranch
    },

    isRelease(state: UserFunctionState) {
      return state.currentBranch === GIT_RELEASE_BRANCH
    },

    myBranchExists(state: UserFunctionState, getters: any, rootState: any) {
      const auth = rootState.auth as AuthState
      const username = auth.user && auth.user.username
      return username && state.currentUserFunctionBranches.includes(username)
    },

    currentUserFunctionHasChanges(state: UserFunctionState) {
      const { currentUserFunction, pristineUserFunction } = state
      const mutableFields = [
        'content',
        'labels',
        'description',
        'unittests',
        'return_type',
        'keys',
        'sample_value',
        'example_code',
        'standalone',
        'deprecated',
        'jira_issue',
      ]
      return (
        JSON.stringify(pick(currentUserFunction, ...mutableFields)) !==
        JSON.stringify(pick(pristineUserFunction, ...mutableFields))
      )
    },

    isOwningOrAdminTeam(state: UserFunctionState, getters: any, rootState: any, rootGetters: any) {
      const auth = rootState.auth as AuthState
      const functionTeamCode = state.currentUserFunction.team_code
      const teamCode = auth.userInfo && auth.userInfo.team_code
      const additionalTeams = auth.userInfo && auth.userInfo.additional_teams

      // Iris team has admin rights
      if (rootGetters['auth/isAdminTeam']) {
        return true
      }
      if (!functionTeamCode) return false

      if (functionTeamCode === teamCode) {
        return true
        // Check if team code belongs to a team group
      } else if (additionalTeams && additionalTeams.length) {
        const teamGroup = additionalTeams.map(el => el.team_code)
        return teamGroup.includes(functionTeamCode)
      } else {
        // Migration of team code after the split of the Consumer Banking Support team
        if (CONSUMER_BANKING_TEAM_CODES.includes(functionTeamCode)) {
          return teamCode && CONSUMER_BANKING_TEAM_CODES.includes(teamCode)
        }
      }
    },

    currentUserFunctionUsedKeys(state: UserFunctionState): UsedKeys {
      return {
        inputs: state.currentUserFunction.keys.map(k => k.name),
        helpers: state.currentUserFunction.content_helpers || [],
      }
    },

    userFunctionHasNoDrafts(state: UserFunctionState) {
      return state.currentUserFunctionBranches && state.currentUserFunctionBranches.length === 1
    },
  }),

  mutations: defineMutations<UserFunctionState>()({
    // This is for unit test usage, so one can setup test state without being mega-intrusive
    REPLACE(state: UserFunctionState, payload: Partial<UserFunctionState>) {
      const keys = Object.keys(payload) as (keyof UserFunctionState)[]

      keys.forEach((key: keyof UserFunctionState) => {
        state[key] = payload[key] as never
      })
    },

    SET_CURRENT_USER_FUNCTION(state: UserFunctionState, { userFunction }: { userFunction: UserFunction }) {
      // Make sure we have a "unittests" property so it's reactive
      if (!userFunction.unittests) {
        userFunction = { ...userFunction, unittests: [] }
      }
      // Make sure we have a "keys" property so that userFunction.keys.filter in SET_USED_KEYS doesn't raise an error
      if (!userFunction.keys) {
        userFunction = { ...userFunction, keys: [] }
      }

      state.currentUserFunction = userFunction
    },

    SET_PRISTINE_CURRENT_USER_FUNCTION(state: UserFunctionState, userFunction: UserFunction) {
      state.pristineUserFunction = userFunction
    },

    SET_USER_FUNCTION_LOADING(state: UserFunctionState, loading: boolean) {
      state.isLoading = loading
    },

    SET_EDITING_TEST_NAME(state: UserFunctionState, isEditingTestName: boolean) {
      state.isEditingTestName = isEditingTestName
    },

    SET_CURRENT_USER_FUNCTION_BRANCHES(state: UserFunctionState, payload: string[]) {
      state.currentUserFunctionBranches = payload
    },

    SET_USER_FUNCTION_SAVING(state: UserFunctionState, saving: boolean) {
      state.isSaving = saving
    },

    SET_CURRENT_USER_FUNCTION_BRANCH(state: UserFunctionState, branch: string) {
      state.currentBranch = branch
    },

    SET_USER_FUNCTION_PULL_REQUEST(state: UserFunctionState, pr: PullRequest | undefined) {
      state.currentPr = pr
    },

    DELETED_USER_FUNCTION_PULL_REQUEST(state: UserFunctionState) {
      state.currentPr = undefined
    },

    UPDATE_CURRENT_USER_FUNCTION(state: UserFunctionState, payload: Partial<UserFunction>) {
      state.currentUserFunction = { ...state.currentUserFunction, ...payload }
    },

    SET_CURRENT_USER_FUNCTION_SCRIPT(state: UserFunctionState, { script }: { script: string }) {
      state.currentUserFunction = {
        ...state.currentUserFunction,
        content: script,
      }
    },

    SET_USER_FUNCTION_RUN_VALUES(state: UserFunctionState, payload: UsedValues) {
      state.usedValues = payload
    },

    SET_CONSOLE_RESULT(state: UserFunctionState, payload?: TestResult) {
      state.consoleResult = payload
    },

    SET_HIGHLIGHTED_FUNCTION(state: UserFunctionState, name?: string) {
      state.functionToHighlight = name
    },

    SET_HIGHLIGHTED_INPUT(state: UserFunctionState, name?: string) {
      state.inputToHighlight = name
    },

    SET_USED_KEYS(
      state: UserFunctionState,
      { functionUsedKeys, isStandalone }: { functionUsedKeys: FunctionUsedKeys; isStandalone: boolean }
    ) {
      // TODO: figure out why SET_USED_KEYS is called when we haven't loaded a function yet
      if (!state.currentUserFunction.id) {
        return
      }

      const { inputs, helpers } = functionUsedKeys
      const func = state.currentUserFunction
      const functionUsedInputs = inputs.map(el => el.name)

      func.content_helpers = helpers
      func.standalone = isStandalone
      func.keys = func.keys.filter(key => functionUsedInputs.includes(key.name))

      const oldInputs = func.keys.map(key => key.name)

      const newInputs = functionUsedInputs.filter(input => !oldInputs.includes(input))

      func.keys = [
        ...func.keys,
        ...newInputs.map(name => ({
          name,
          type: 'str' as DataType,
          required: true,
          initiallyMockable: true,
        })),
      ]
      // Updating user function keys' required property from the /lint endpoint
      func.keys.forEach(el => (el.required = inputs.find(v => v.name === el.name)!.required))
    },

    SET_INSERT_FUNCTION(
      state: UserFunctionState,
      payload?: {
        functionName: string
        usageExample: string
        functionType?: string
      }
    ) {
      state.insertedFunction = payload
    },

    SET_USER_FUNCTION_TESTS(state: UserFunctionState, payload: UserFunctionTest[]) {
      state.currentUserFunction.unittests = payload
    },

    REORDER_USER_FUNCTION_TESTS(state: UserFunctionState, payload: { fromIndex: number; toIndex: number }) {
      const { fromIndex, toIndex } = payload
      if (!state.currentUserFunction.unittests) return

      const test = state.currentUserFunction.unittests[fromIndex]
      state.currentUserFunction.unittests.splice(fromIndex, 1)
      state.currentUserFunction.unittests.splice(toIndex, 0, test)
    },

    UPDATE_USER_FUNCTION_TEST(state: UserFunctionState, { index, test }: { index: number; test: UserFunctionTest }) {
      const { unittests = [] } = state.currentUserFunction
      const newUnitTests = [...unittests]
      newUnitTests[index] = test
      state.currentUserFunction.unittests = newUnitTests
    },

    SET_USER_FUNCTION_TEST_RESULTS(state: UserFunctionState, testResults: (TestResult | undefined)[] | undefined) {
      state.testResults = testResults
    },

    SET_DRAFT_IS_OUTDATED(state: UserFunctionState, status: boolean) {
      state.isDraftOutdated = status
    },

    SET_IS_PR_MERGING(state: UserFunctionState, isPRMerging: boolean) {
      state.isPRMerging = isPRMerging
    },

    SET_IS_PR_LOADING(state: UserFunctionState, loading: boolean) {
      state.isPRLoading = loading
    },
  }),

  actions: defineActions({
    async CREATE_USER_FUNCTION(
      ctx,
      userFunction: CreateFunctionRequest & {
        keys?: ParamKeyMeta[]
        unittests?: UserFunctionTest[]
        content_helpers?: string[] | undefined
        content?: string | undefined
        return_type?: DataType
        sample_value?: any
        example_code?: string
        standalone?: boolean
      }
    ): Promise<UserFunction> {
      const {
        commit,
        rootState: { scriptKey },
        rootDispatch,
      } = userFunctionActionContext(ctx)
      commit.SET_USER_FUNCTION_LOADING(true)
      const newUserFunction: CreateFunctionRequest = {
        jira_issue: userFunction.jira_issue,
        id: userFunction.id,
        description: userFunction.description,
        labels: userFunction.labels || [],
        format_version: userFunction.format_version || LATEST_SUPPORTED_USER_FUNCTION_FORMAT_VERSION,
      }

      try {
        const { data: savedUserFunction } = await ApiService.createUserFunction(newUserFunction)
        // Ensure a draft branch exists
        const { data: userFunctionDraft } = await ApiService.updateUserFunction(savedUserFunction.id, {
          ...savedUserFunction,
          content: userFunction.content || savedUserFunction.content,
          unittests: userFunction.unittests || [],
          keys: userFunction.keys || [],
          content_helpers: userFunction.content_helpers || [],
          return_type: userFunction.return_type || savedUserFunction.return_type,
          sample_value: userFunction.sample_value || savedUserFunction.sample_value,
          example_code: userFunction.example_code || savedUserFunction.example_code,
          standalone: userFunction.standalone || savedUserFunction.standalone,
          commit_message: 'create draft',
        })

        if (!scriptKey.inputs) {
          await rootDispatch.scriptKey.GET_INPUTS_METADATA()
        }

        if (!scriptKey.helpers) {
          await rootDispatch.scriptKey.GET_HELPERS_METADATA()
        }

        return userFunctionDraft
      } finally {
        commit.SET_USER_FUNCTION_LOADING(false)
      }
    },

    async GET_USER_FUNCTION(
      ctx,
      { userFunctionId, branch }: { userFunctionId: string; branch?: string }
    ): Promise<UserFunction> {
      const { data } = await ApiService.getUserFunction(userFunctionId, branch || GIT_RELEASE_BRANCH)
      return data
    },

    async GET_CURRENT_USER_FUNCTION(
      ctx,
      { userFunctionId, branch }: { userFunctionId: string; branch?: string }
    ): Promise<UserFunction> {
      const {
        commit,
        dispatch,
        rootState: {
          auth: { user },
        },
      } = userFunctionActionContext(ctx)
      commit.SET_USER_FUNCTION_LOADING(true)
      // Reset anything related to previously loaded user function
      commit.SET_CURRENT_USER_FUNCTION({ userFunction: {} as UserFunction })
      commit.SET_CONSOLE_RESULT(undefined)
      commit.SET_USER_FUNCTION_RUN_VALUES({ inputs: {}, helpers: {} })
      commit.SET_USER_FUNCTION_PULL_REQUEST(undefined)
      commit.SET_DRAFT_IS_OUTDATED(false)

      const branchesPromise = dispatch.GET_CURRENT_USER_FUNCTION_BRANCHES(userFunctionId)
      if (!branch) {
        const branches = await branchesPromise
        branch = branches.includes(user!.username) ? user!.username : GIT_RELEASE_BRANCH
      }

      commit.SET_CURRENT_USER_FUNCTION_BRANCH(branch!)

      try {
        const { data: userFunction } = await ApiService.getUserFunction(userFunctionId, branch!)

        const storedUserFunction: string | null = window.localStorage.getItem(restoreKey(userFunctionId, branch!))

        if (
          storedUserFunction &&
          JSON.parse(storedUserFunction).format_version === LATEST_SUPPORTED_USER_FUNCTION_FORMAT_VERSION
        ) {
          const useStoredUserFunction = window.confirm(
            'Looks like you have an unsaved version of this rule locally, restore it?'
          )
          if (useStoredUserFunction) {
            const currentUserFunction = JSON.parse(storedUserFunction)
            commit.SET_CURRENT_USER_FUNCTION({
              userFunction: currentUserFunction,
            })
          } else {
            commit.SET_CURRENT_USER_FUNCTION({ userFunction })
            window.localStorage.removeItem(restoreKey(userFunctionId, branch!))
          }
        } else {
          commit.SET_CURRENT_USER_FUNCTION({ userFunction })
        }
        commit.SET_PRISTINE_CURRENT_USER_FUNCTION(deepClone(userFunction))
        return userFunction
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          // eslint-disable-next-line prettier/prettier
          ;(err as any).silent = true
        }
        throw err
      } finally {
        commit.SET_USER_FUNCTION_LOADING(false)
      }
    },

    async GET_CURRENT_USER_FUNCTION_BRANCHES(ctx, id: string): Promise<string[]> {
      const { commit } = userFunctionActionContext(ctx)
      const { data: result } = await ApiService.getUserFunctionBranches(id)
      const branches = result.map((b: string) => b.replace('draft/', ''))
      commit.SET_CURRENT_USER_FUNCTION_BRANCHES(branches)
      return branches
    },

    async SAVE_CURRENT_USER_FUNCTION(
      ctx,
      args?: {
        commitMessage?: string
        operationType: 'SAVE_SILENTLY' | 'SAVE' | 'SAVE_BEFORE_PR' | 'CREATE_NEW_BRANCH'
      }
    ): Promise<UserFunction> {
      const { commitMessage = 'save', operationType = 'SAVE_SILENTLY' } = args || {}

      const { commit, state, rootDispatch, rootState, rootGetters, getters } = userFunctionActionContext(ctx)

      if (['SAVE', 'SAVE_SILENTLY', 'SAVE_BEFORE_PR'].includes(operationType)) {
        const hasChange = getters.currentUserFunctionHasChanges
        if (!hasChange && operationType === 'SAVE') {
          rootDispatch.SHOW_NOTIFICATION({
            message: notifications.skipSave,
            state: 'info',
          })
        }

        if (!hasChange) {
          return state.currentUserFunction
        }
      }

      const { currentUserFunction } = state
      const { username } = rootState.auth.user!

      const defaultValueProvider = buildDefaultValueProvider(
        rootGetters.scriptKey.scriptKeysLookup,
        currentUserFunction
      )

      const defaultReturnValue = testValueUtils.getDefaultValueForDataType(currentUserFunction.return_type)

      const sanitizedUnitTests: UserFunctionTest[] = testValueUtils
        .sanitizeTestsWithValueProvider(
          rootGetters.scriptKey.scriptKeysLookup,
          defaultValueProvider,
          currentUserFunction.unittests || [],
          getters.currentUserFunctionUsedKeys
        )
        // Ensure the expected result value is defined for all unit tests
        .map(
          (unitTest: UserFunctionTest): UserFunctionTest => ({
            ...unitTest,
            result: {
              value: typeof unitTest.result.value === 'undefined' ? defaultReturnValue : unitTest.result.value,
            },
          })
        )

      const userFunctionCommit: UserFunction = {
        ...currentUserFunction,
        standalone: currentUserFunction.standalone || false,
        unittests: sanitizedUnitTests,
        commit_message: commitMessage,
      }

      commit.SET_USER_FUNCTION_LOADING(true)
      commit.SET_USER_FUNCTION_SAVING(true)

      try {
        const { data: newUserFunction } = await ApiService.updateUserFunction(
          currentUserFunction.id,
          userFunctionCommit
        )

        window.localStorage.removeItem(restoreKey(currentUserFunction.id, currentUserFunction.branch_name))

        const updatedUserFunction: UserFunction = {
          // TODO: we only get partial userFunction information after the PUT request
          ...state.currentUserFunction,
          ...newUserFunction,
        }
        commit.SET_CURRENT_USER_FUNCTION_BRANCH(username)

        commit.SET_CURRENT_USER_FUNCTION({
          userFunction: {
            ...updatedUserFunction,
          },
        })

        commit.SET_PRISTINE_CURRENT_USER_FUNCTION(deepClone(updatedUserFunction))

        if (operationType !== 'SAVE_SILENTLY') {
          rootDispatch.SHOW_NOTIFICATION({
            message: {
              SAVE: notifications.successfullySave,
              SAVE_BEFORE_PR: notifications.successfullySave,
              CREATE_NEW_BRANCH: notifications.successfullyCreateDraft,
            }[operationType],
          })
        }
        return updatedUserFunction
      } finally {
        commit.SET_USER_FUNCTION_LOADING(false)
        commit.SET_USER_FUNCTION_SAVING(false)
      }
    },

    async DELETE_CURRENT_DRAFT(ctx, { userFunctionId, branch }: { userFunctionId: string; branch: string }) {
      const { commit, rootDispatch } = userFunctionActionContext(ctx)
      commit.SET_USER_FUNCTION_LOADING(true)
      try {
        await ApiService.deleteUserFunctionBranch(userFunctionId, branch)
        localStorage.removeItem(restoreKey(userFunctionId, branch))

        rootDispatch.SHOW_NOTIFICATION({
          message: notifications.successfullyDeleteDraft,
          state: 'info',
        })

        // Clear state immediately so we don't trigger an update event for
        // the recently deleted draft before redirecting users to the release branch
        commit.SET_CURRENT_USER_FUNCTION({ userFunction: {} as UserFunction })
        getRouter()?.push(
          paths.userFunctionBuilder.replace(':user_function_id', userFunctionId).replace(':branch?', GIT_RELEASE_BRANCH)
        )
      } finally {
        commit.SET_USER_FUNCTION_LOADING(false)
      }
    },

    async DELETE_USER_FUNCTION(ctx, userFunctionId: string) {
      const { commit, rootDispatch } = userFunctionActionContext(ctx)
      commit.SET_USER_FUNCTION_LOADING(true)
      try {
        await ApiService.deleteUserFunction(userFunctionId)
        rootDispatch.SHOW_NOTIFICATION({
          message: notifications.successfullyDeleteUserFunction,
          state: 'info',
        })
        getRouter()?.push(paths.home)
      } finally {
        commit.SET_USER_FUNCTION_LOADING(false)
      }
    },

    async OPEN_USER_FUNCTION_PR(ctx, payload: { description: string }): Promise<PullRequest> {
      const { state, commit, dispatch } = userFunctionActionContext(ctx)
      const {
        currentUserFunction: { id },
        currentBranch,
      } = state

      commit.SET_USER_FUNCTION_LOADING(true)

      await dispatch.SAVE_CURRENT_USER_FUNCTION({
        operationType: 'SAVE_BEFORE_PR',
      })

      try {
        const { data: pullRequest } = await ApiService.openUserFunctionPullRequest(id, currentBranch, payload)

        commit.SET_USER_FUNCTION_PULL_REQUEST(pullRequest)
        return pullRequest
      } finally {
        commit.SET_USER_FUNCTION_LOADING(false)
        dispatch.POLL_USER_FUNCTION_PR_STATUS({
          userFunctionId: id,
          branch: currentBranch,
        })
      }
    },

    async DELETE_USER_FUNCTION_PR(ctx): Promise<void> {
      const { state, commit } = userFunctionActionContext(ctx)
      const {
        currentUserFunction: { id },
        currentBranch,
      } = state

      commit.SET_USER_FUNCTION_LOADING(true)

      await ApiService.deleteFunctionPullRequest(id, currentBranch)

      commit.DELETED_USER_FUNCTION_PULL_REQUEST()
      commit.SET_USER_FUNCTION_LOADING(false)
    },

    POLL_USER_FUNCTION_PR_STATUS(
      ctx,
      { userFunctionId, branch }: { userFunctionId: string; branch: string }
    ): Promise<string> {
      const { commit, dispatch, state, rootDispatch } = userFunctionActionContext(ctx)
      const { currentBranch } = state

      return new Promise(resolve => {
        return dispatch
          .GET_CURRENT_USER_FUNCTION_BRANCHES(userFunctionId)
          .then(async res => {
            if (!res.includes(currentBranch)) {
              commit.SET_USER_FUNCTION_PULL_REQUEST(undefined)
              getRouter()?.push(
                paths.userFunctionBuilder
                  .replace(':user_function_id', userFunctionId)
                  .replace(':branch?', GIT_RELEASE_BRANCH)
              )
              commit.SET_IS_PR_MERGING(false)
              rootDispatch.SHOW_NOTIFICATION({
                message: notifications.successfullyMerged(userFunctionId),
              })
              return resolve(status)
            }

            // Check if PR has been approved
            const { data: pullRequest } = await ApiService.getUserFunctionPullRequest(
              userFunctionId,
              draftBranchName(branch)
            )

            if (pullRequest.state === 'OPEN') commit.SET_USER_FUNCTION_PULL_REQUEST(pullRequest)

            // uncomment it to enable poll mechanism (CBRMS-2147)
            //setTimeout(
            //  () =>
            //    dispatch.POLL_USER_FUNCTION_PR_STATUS({
            //      userFunctionId,
            //      branch
            //    }),
            //  10000
            //);
          })
          .catch(() => undefined)
      })
    },

    async MERGE_PULL_REQUEST(ctx): Promise<void> {
      const { commit, state, dispatch } = userFunctionActionContext(ctx)
      const { currentUserFunction } = state

      if (!state.currentPr) return

      commit.SET_IS_PR_MERGING(true)
      try {
        await ApiService.mergeUserFunctionPullRequest(currentUserFunction.id, state.currentBranch, {
          id: state.currentPr.id,
          version: state.currentPr.version,
        })

        dispatch.POLL_USER_FUNCTION_PR_STATUS({
          userFunctionId: currentUserFunction.id,
          branch: state.currentBranch,
        })
      } catch (e) {
        commit.SET_IS_PR_MERGING(false)
        throw new Error(
          `We couldn't merge your Pull Request.
          Please <strong>make sure all required checks are passing</strong>, then try again.<br><br>
          If the issue persists, please <strong>contact us</strong>.`
        )
      }
    },

    async GET_USER_FUNCTION_PR(
      ctx,
      { userFunctionId, branch }: { userFunctionId: string; branch: string }
    ): Promise<PullRequest | undefined> {
      const {
        rootState: { auth },
        commit,
        dispatch,
      } = userFunctionActionContext(ctx)

      try {
        commit.SET_IS_PR_LOADING(true)
        const { data: pullRequest } = await ApiService.getUserFunctionPullRequest(
          userFunctionId,
          draftBranchName(branch)
        )

        if (pullRequest.state === 'OPEN' && branch === auth.user!.username) {
          dispatch.POLL_USER_FUNCTION_PR_STATUS({ userFunctionId, branch })
        }
        commit.SET_USER_FUNCTION_PULL_REQUEST(pullRequest)

        return pullRequest
      } finally {
        commit.SET_IS_PR_LOADING(false)
      }
    },

    async FORMAT_SCRIPT(ctx): Promise<void> {
      const { state, commit } = userFunctionActionContext(ctx)

      const { data: result } = await ApiService.formatScript({
        script: state.currentUserFunction.content,
      })

      commit.SET_CURRENT_USER_FUNCTION_SCRIPT(result as any)
    },

    async RUN_USER_FUNCTION(ctx) {
      const { commit, state, getters, rootGetters } = userFunctionActionContext(ctx)
      const { usedValues, currentUserFunction } = state

      const defaultValueProvider = buildDefaultValueProvider(
        rootGetters.scriptKey.scriptKeysLookup,
        currentUserFunction
      )

      const { inputs, helpers } = testValueUtils.sanitizeTestWithValueProvider(
        rootGetters.scriptKey.scriptKeysLookup,
        defaultValueProvider,
        usedValues,
        getters.currentUserFunctionUsedKeys
      )

      const userFunctionId = state.currentUserFunction.id

      const payload = {
        id: userFunctionId,
        inputs,
        helpers,
        script: state.currentUserFunction.content,
      }
      commit.SET_CONSOLE_RESULT(undefined)
      const { data: result } = await ApiService.getUserFunctionExecutionResult(payload)
      commit.SET_CONSOLE_RESULT(result)
    },

    ADD_USER_FUNCTION_TEST(ctx, test: UserFunctionTest): void {
      const { commit, state } = userFunctionActionContext(ctx)
      const unittests = state.currentUserFunction.unittests || []
      commit.SET_USER_FUNCTION_TESTS([...unittests, test])
    },

    REMOVE_USER_FUNCTION_TEST(ctx, idxToRemove: number): void {
      const { commit, state } = userFunctionActionContext(ctx)

      const unittests = state.currentUserFunction.unittests || []
      commit.SET_USER_FUNCTION_TESTS(unittests.filter((_: UserFunctionTest, idx: number) => idx !== idxToRemove))
    },

    MOVE_USER_FUNCTION_TEST(ctx, { fromIndex, toIndex }: { fromIndex: number; toIndex: number }): void {
      const { commit, state } = userFunctionActionContext(ctx)
      const unittests = [...state.currentUserFunction.unittests!]

      unittests.splice(toIndex, 0, unittests.splice(fromIndex, 1)[0])
      commit.SET_USER_FUNCTION_TESTS(unittests)

      // If there are test results, move them along with the userFunction test.
      const testResults = [...(state.testResults || [])]
      testResults.splice(toIndex, 0, testResults.splice(fromIndex, 1)[0])
      commit.SET_USER_FUNCTION_TEST_RESULTS(testResults)
    },

    async RUN_USER_FUNCTION_TESTS(ctx): Promise<TestResult[] | undefined> {
      const { commit, state, getters, rootGetters } = userFunctionActionContext(ctx)
      const { currentUserFunction } = state

      const defaultValueProvider = buildDefaultValueProvider(
        rootGetters.scriptKey.scriptKeysLookup,
        currentUserFunction
      )

      const returnType = currentUserFunction.return_type
      const defaultReturnValue = testValueUtils.getDefaultValueForDataType(returnType)

      const sanitizedUnitTests: UserFunctionTest[] = testValueUtils
        .sanitizeTestsWithValueProvider(
          rootGetters.scriptKey.scriptKeysLookup,
          defaultValueProvider,
          currentUserFunction.unittests || [],
          getters.currentUserFunctionUsedKeys
        )
        // Ensure the expected result value is defined for all unit tests
        .map(
          (unitTest: UserFunctionTest): UserFunctionTest => ({
            ...unitTest,
            result: {
              value: typeof unitTest.result.value === 'undefined' ? defaultReturnValue : unitTest.result.value,
            },
          })
        )

      const { data: testResults } = await ApiService.getUserFunctionTestResults({
        id: state.currentUserFunction.id,
        unittests: sanitizedUnitTests,
        script: state.currentUserFunction.content,
      })

      commit.SET_USER_FUNCTION_TEST_RESULTS(testResults)

      return testResults
    },

    AUTOSAVE_IN_LOCALSTORAGE(ctx) {
      const { state, getters } = userFunctionActionContext(ctx)
      const key = restoreKey(state.currentUserFunction.id, state.currentUserFunction.branch_name)
      if (getters.currentUserFunctionHasChanges) {
        window.localStorage.setItem(key, JSON.stringify(deepClone(state.currentUserFunction)))
      } else {
        window.localStorage.removeItem(key)
      }
    },

    UPDATE_CURRENT_USER_FUNCTION(ctx, payload: Partial<UserFunction>) {
      const { commit, dispatch } = userFunctionActionContext(ctx)
      commit.UPDATE_CURRENT_USER_FUNCTION(payload)
      dispatch.AUTOSAVE_IN_LOCALSTORAGE()
    },

    UPDATE_USER_FUNCTION_TESTS(ctx, { index, test }: { index: number; test: UserFunctionTest }) {
      const { commit, dispatch } = userFunctionActionContext(ctx)
      commit.UPDATE_USER_FUNCTION_TEST({ index, test })
      dispatch.AUTOSAVE_IN_LOCALSTORAGE()
    },

    async POLL_IS_CURRENT_DRAFT_OUTDATED(ctx, { userFunctionId, branch }: { userFunctionId: string; branch: string }) {
      const { dispatch, commit } = userFunctionActionContext(ctx)

      const res = await dispatch.GET_USER_FUNCTION({ userFunctionId, branch })
      const sourceCommit = res.source_commit
      const latestReleaseCommit = res.latest_release_commit

      if (sourceCommit && sourceCommit !== latestReleaseCommit) {
        commit.SET_DRAFT_IS_OUTDATED(true)
        return
      }

      dispatch.CLEAR_TIMEOUT()

      pollingTimer = window.setTimeout(() => dispatch.POLL_IS_CURRENT_DRAFT_OUTDATED({ userFunctionId, branch }), 60000)
    },

    CLEAR_TIMEOUT() {
      clearTimeout(pollingTimer)
    },
  }),
})

const buildDefaultValueProvider = (scriptKeysLookup: ScriptKeysLookup, userFunc: UserFunction) => {
  return (keyType: KeyType, keyName: string) => {
    if (keyType === 'helper') {
      return testValueUtils.getDefaultValue(scriptKeysLookup, keyName, 'helper')
    }

    const dataType = userFunc.keys.find(k => k.name === keyName)!.type

    return testValueUtils.getDefaultValueForDataType(dataType)
  }
}

export default userFunctionModule
